/**
 * OrgDefaultPrizeAdjustmentsSection
 *
 * Org-settings section (rendered inside the Edit Organization modal) for
 * managing an organization's DEFAULT prize adjustments. These auto-apply to
 * every event in the org's rodeos: each event inherits them (on by default,
 * toggleable per event, amount-overridable per event, wording locked, not
 * deletable). Net-new per-event adjustments are still managed on the event
 * itself.
 *
 * An adjustment is either a fixed dollar amount OR a percentage. The info note
 * explains that a percentage default applied to a dynamic-prize event is taken
 * on the summed total (prize + fixed adjustments + entry-fee contribution),
 * matching the shared calculation engine.
 *
 * Two modes:
 *   • Live  — pass `organizationId`; every change hits the prize-ops API for an
 *             existing org (the Edit Organization modal).
 *   • Draft — pass `draftController`; the org doesn't exist yet (the Create
 *             Organization modal), so the per-entry contribution and the
 *             adjustments list are buffered in parent state instead of saved.
 *             The parent persists them once the org is created.
 */

import { useState } from 'react';
import { Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { notify } from '../../utils/notifications';
import { formatCurrency } from '../../utils/formatters';
import {
  useOrgDefaultPrizeAdjustments,
  useCreateOrgDefaultPrizeAdjustment,
  useUpdateOrgDefaultPrizeAdjustment,
  useDeleteOrgDefaultPrizeAdjustment,
  useOrgPrizeSettings,
  useUpdateOrgPrizeSettings,
  type OrgDefaultPrizeAdjustment,
} from '../../api/extensions/usePrizeOps';
import { useEffect } from 'react';

type Operation = 'addition' | 'subtraction';
type Kind = 'fixed' | 'percentage';

interface DraftState {
  adjustment_type: Operation;
  kind: Kind;
  amount: string;
  percentage: string;
  reason: string;
}

const EMPTY_DRAFT: DraftState = {
  adjustment_type: 'subtraction',
  kind: 'fixed',
  amount: '',
  percentage: '',
  reason: '',
};

type PerEntryMode = 'amount' | 'percentage' | 'fee_deduction';

/**
 * Buffers the org-default prize settings for a not-yet-created org. The Create
 * Organization modal owns this state and persists it after the org insert
 * returns an id.
 */
export interface OrgPrizeDraftController {
  perEntry: string;
  setPerEntry: (v: string) => void;
  perEntryMode: PerEntryMode;
  setPerEntryMode: (m: PerEntryMode) => void;
  adjustments: OrgDefaultPrizeAdjustment[];
  setAdjustments: (next: OrgDefaultPrizeAdjustment[]) => void;
}

function describeAdjustment(a: OrgDefaultPrizeAdjustment): string {
  const sign = a.adjustment_type === 'addition' ? '+' : '−';
  const value =
    a.percentage !== null && a.percentage !== undefined
      ? `${a.percentage}%`
      : formatCurrency(a.amount);
  return `${sign}${value}`;
}

export function OrgDefaultPrizeAdjustmentsSection({
  organizationId,
  draftController,
}: {
  organizationId?: string;
  draftController?: OrgPrizeDraftController;
}) {
  const isDraft = !!draftController;
  const { data: defaults, isLoading } = useOrgDefaultPrizeAdjustments(organizationId);
  const createMut = useCreateOrgDefaultPrizeAdjustment();
  const updateMut = useUpdateOrgDefaultPrizeAdjustment();
  const deleteMut = useDeleteOrgDefaultPrizeAdjustment(organizationId ?? '');

  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<DraftState>({ ...EMPTY_DRAFT });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftState>({ ...EMPTY_DRAFT });
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  // Adjustments come from the API (live) or the parent buffer (draft).
  const adjustments: OrgDefaultPrizeAdjustment[] = isDraft
    ? draftController!.adjustments
    : defaults ?? [];
  const listLoading = isDraft ? false : isLoading;

  // Org-level default per-entry prize contribution (pre-fills new dynamic events).
  const { data: prizeSettings } = useOrgPrizeSettings(organizationId);
  const updateSettingsMut = useUpdateOrgPrizeSettings();
  const [localPerEntry, setLocalPerEntry] = useState('');
  // Flat $ amount, % of entry fee, or entry fee MINUS a fixed amount the org keeps.
  const [localPerEntryMode, setLocalPerEntryMode] = useState<PerEntryMode>('amount');
  // Route the per-entry inputs through the parent buffer in draft mode.
  const perEntry = isDraft ? draftController!.perEntry : localPerEntry;
  const setPerEntry = isDraft ? draftController!.setPerEntry : setLocalPerEntry;
  const perEntryMode = isDraft ? draftController!.perEntryMode : localPerEntryMode;
  const setPerEntryMode = isDraft ? draftController!.setPerEntryMode : setLocalPerEntryMode;
  useEffect(() => {
    if (isDraft || !prizeSettings) return;
    setLocalPerEntry(
      prizeSettings.default_entry_fee_prize_portion > 0
        ? String(prizeSettings.default_entry_fee_prize_portion)
        : '',
    );
    setLocalPerEntryMode(
      prizeSettings.default_entry_fee_prize_portion_is_fee_deduction
        ? 'fee_deduction'
        : prizeSettings.default_entry_fee_prize_portion_is_percentage
          ? 'percentage'
          : 'amount',
    );
  }, [isDraft, prizeSettings]);

  const handleSavePerEntry = async () => {
    try {
      await updateSettingsMut.mutateAsync({
        // Live mode only (this handler is unreachable while isDraft), so the
        // optional prop is present here — same assertion as createMut below.
        organizationId: organizationId!,
        settings: {
          default_entry_fee_prize_portion: perEntry ? Number(perEntry) || 0 : 0,
          default_entry_fee_prize_portion_is_percentage: perEntryMode === 'percentage',
          default_entry_fee_prize_portion_is_fee_deduction: perEntryMode === 'fee_deduction',
        },
      });
      notify.success('Default per-entry contribution saved');
    } catch (e: any) {
      notify.error(e?.message || 'Failed to save default');
    }
  };

  const buildPayload = (d: DraftState) => {
    const isPct = d.kind === 'percentage';
    return {
      adjustment_type: d.adjustment_type,
      amount: isPct ? 0 : Number(d.amount) || 0,
      percentage: isPct ? Number(d.percentage) || 0 : null,
      reason: d.reason.trim(),
    };
  };

  const validate = (d: DraftState): string | null => {
    if (!d.reason.trim()) return 'Enter a label for this adjustment';
    if (d.kind === 'fixed' && !(Number(d.amount) > 0)) return 'Enter a dollar amount greater than 0';
    if (d.kind === 'percentage') {
      const p = Number(d.percentage);
      if (!(p > 0) || p > 100) return 'Enter a percentage between 0 and 100';
    }
    return null;
  };

  const handleCreate = async () => {
    const err = validate(addDraft);
    if (err) return notify.error(err);
    const payload = buildPayload(addDraft);
    if (isDraft) {
      const newAdjustment: OrgDefaultPrizeAdjustment = {
        id: `draft-${draftController!.adjustments.length}-${addDraft.reason}`,
        organization_id: '',
        adjustment_type: payload.adjustment_type,
        amount: payload.amount,
        percentage: payload.percentage,
        reason: payload.reason,
        description: null,
        is_active: true,
        sort_order: draftController!.adjustments.length,
        created_at: '',
        updated_at: '',
      };
      draftController!.setAdjustments([...draftController!.adjustments, newAdjustment]);
      notify.success('Default prize adjustment added');
      setAddDraft({ ...EMPTY_DRAFT });
      setAdding(false);
      return;
    }
    try {
      await createMut.mutateAsync({ organization_id: organizationId!, ...payload });
      notify.success('Default prize adjustment added');
      setAddDraft({ ...EMPTY_DRAFT });
      setAdding(false);
    } catch (e: any) {
      notify.error(e?.message || 'Failed to add adjustment');
    }
  };

  const startEdit = (a: OrgDefaultPrizeAdjustment) => {
    const isPct = a.percentage !== null && a.percentage !== undefined;
    setEditingId(a.id);
    setEditDraft({
      adjustment_type: a.adjustment_type,
      kind: isPct ? 'percentage' : 'fixed',
      amount: isPct || a.amount == null ? '' : Number(a.amount).toFixed(2),
      percentage: isPct ? String(a.percentage ?? '') : '',
      reason: a.reason,
    });
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    const err = validate(editDraft);
    if (err) return notify.error(err);
    const updates = buildPayload(editDraft);
    if (isDraft) {
      draftController!.setAdjustments(
        draftController!.adjustments.map((a) =>
          a.id === editingId ? { ...a, ...updates } : a,
        ),
      );
      notify.success('Default prize adjustment updated');
      setEditingId(null);
      return;
    }
    try {
      await updateMut.mutateAsync({ id: editingId, updates });
      notify.success('Default prize adjustment updated');
      setEditingId(null);
    } catch (e: any) {
      notify.error(e?.message || 'Failed to update adjustment');
    }
  };

  const handleToggleActive = async (a: OrgDefaultPrizeAdjustment, isActive: boolean) => {
    if (isDraft) {
      draftController!.setAdjustments(
        draftController!.adjustments.map((x) =>
          x.id === a.id ? { ...x, is_active: isActive } : x,
        ),
      );
      return;
    }
    try {
      await updateMut.mutateAsync({ id: a.id, updates: { is_active: isActive } });
    } catch (e: any) {
      notify.error(e?.message || 'Failed to update adjustment');
    }
  };

  const handleDelete = async (id: string) => {
    if (isDraft) {
      draftController!.setAdjustments(
        draftController!.adjustments.filter((x) => x.id !== id),
      );
      notify.success('Default prize adjustment removed');
      setConfirmingDeleteId(null);
      return;
    }
    try {
      await deleteMut.mutateAsync(id);
      notify.success('Default prize adjustment removed');
      setConfirmingDeleteId(null);
    } catch (e: any) {
      notify.error(e?.message || 'Failed to remove adjustment');
    }
  };

  const renderDraftForm = (
    state: DraftState,
    setState: (s: DraftState) => void,
    onSave: () => void,
    onCancel: () => void,
    saving: boolean,
    saveLabel: string,
  ) => (
    <div className="rounded-md border border-border p-3 space-y-3 bg-muted/30">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="app-form-label">Direction</label>
          <select
            className="app-input w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={state.adjustment_type}
            onChange={(e) => setState({ ...state, adjustment_type: e.target.value as Operation })}
          >
            <option value="subtraction">Subtract from prize</option>
            <option value="addition">Add to prize</option>
          </select>
        </div>
        <div>
          <label className="app-form-label">Type</label>
          <select
            className="app-input w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={state.kind}
            onChange={(e) => setState({ ...state, kind: e.target.value as Kind })}
          >
            <option value="fixed">Fixed amount ($)</option>
            <option value="percentage">Percentage (%)</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {state.kind === 'fixed' ? (
          <div>
            <label className="app-form-label">Amount</label>
            <CurrencyInput
              value={state.amount}
              onChange={(v) => setState({ ...state, amount: v })}
              placeholder="0.00"
            />
          </div>
        ) : (
          <div>
            <label className="app-form-label">Percentage</label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={state.percentage}
              onChange={(e) => setState({ ...state, percentage: e.target.value })}
              placeholder="0"
            />
          </div>
        )}
        <div>
          <label className="app-form-label">Label (wording)</label>
          <Input
            value={state.reason}
            onChange={(e) => setState({ ...state, reason: e.target.value })}
            placeholder="e.g., Stock charge"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Default per-entry prize contribution — pre-fills new dynamic events. */}
      <div className="app-form-field">
        <label className="app-form-label">Default per-entry prize contribution</label>
        <p className="app-form-help">
          Pre-fills the per-entry amount on new events that use dynamic prize allocation. Choose
          how it's interpreted: a flat dollar amount per entry, a percentage of the entry fee, or
          the entry fee MINUS a fixed amount the org keeps (e.g. "fee − $40" puts $110 of a $150
          entry into the prize, $60 of a $100 entry). Organizers can override it per event.
        </p>
        <div className="mt-2 inline-flex rounded-md border border-input overflow-hidden">
          <button
            type="button"
            onClick={() => setPerEntryMode('amount')}
            className={`px-2.5 py-1.5 text-sm transition-colors ${
              perEntryMode === 'amount'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent'
            }`}
          >
            $ flat
          </button>
          <button
            type="button"
            onClick={() => setPerEntryMode('percentage')}
            className={`px-2.5 py-1.5 text-sm border-l border-input transition-colors ${
              perEntryMode === 'percentage'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent'
            }`}
          >
            % of fee
          </button>
          <button
            type="button"
            onClick={() => setPerEntryMode('fee_deduction')}
            className={`px-2.5 py-1.5 text-sm border-l border-input transition-colors ${
              perEntryMode === 'fee_deduction'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent'
            }`}
          >
            Fee − $
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2">
          {perEntryMode === 'percentage' ? (
            <div className="flex items-center gap-1 flex-1">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={perEntry}
                onChange={(e) => setPerEntry(e.target.value)}
                placeholder="0"
              />
              <span className="text-sm text-muted-foreground">% of entry fee</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 flex-1">
              <CurrencyInput value={perEntry} onChange={setPerEntry} placeholder="0.00" />
              {perEntryMode === 'fee_deduction' && (
                <span className="text-sm text-muted-foreground">kept from each entry fee</span>
              )}
            </div>
          )}
          {/* In draft mode the value is persisted with "Create Organization",
              so there's no standalone Save here. */}
          {!isDraft && (
            <Button
              type="button"
              size="sm"
              onClick={handleSavePerEntry}
              disabled={updateSettingsMut.isPending}
            >
              {updateSettingsMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      <div className="app-form-field">
      <div className="flex items-center justify-between">
        <label className="app-form-label">Default Prize Adjustments</label>
        {!adding && (
          <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)} className="h-7 text-xs">
            + Add Default
          </Button>
        )}
      </div>
      <p className="app-form-help">
        These apply automatically to every event in this organization's rodeos. Each event can toggle
        them on/off and override the amount, but cannot delete them or change the wording. Dollar
        adjustments apply first; a percentage default is then taken on the resulting subtotal.
      </p>

      <div className="mt-2 space-y-2">
        {listLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!listLoading && adjustments.length === 0 && !adding && (
          <div className="text-sm text-muted-foreground/70">No default prize adjustments yet.</div>
        )}

        {adjustments.map((a) =>
          editingId === a.id ? (
            <div key={a.id}>
              {renderDraftForm(
                editDraft,
                setEditDraft,
                handleUpdate,
                () => setEditingId(null),
                updateMut.isPending,
                'Save',
              )}
            </div>
          ) : (
            <div
              key={a.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border p-2.5"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{a.reason}</div>
                <div className="text-xs text-muted-foreground">
                  {describeAdjustment(a)}
                  {!a.is_active && ' · inactive'}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch
                  checked={a.is_active}
                  onCheckedChange={(v) => handleToggleActive(a, v)}
                  aria-label={`Toggle ${a.reason}`}
                />
                <button
                  type="button"
                  onClick={() => startEdit(a)}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                  aria-label={`Edit ${a.reason}`}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                {confirmingDeleteId === a.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleDelete(a.id)}
                      className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingDeleteId(null)}
                      className="text-xs px-2 py-1 rounded border border-border"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingDeleteId(a.id)}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent transition-colors"
                    aria-label={`Delete ${a.reason}`}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ),
        )}

        {adding &&
          renderDraftForm(
            addDraft,
            setAddDraft,
            handleCreate,
            () => {
              setAdding(false);
              setAddDraft({ ...EMPTY_DRAFT });
            },
            createMut.isPending,
            'Add',
          )}
      </div>
      </div>
    </div>
  );
}
